import { useState } from "react";

import { ApiError } from "@/api/client";
import {
  useEndPreviewFinalSummaryMutation,
  usePreviewFinalSummaryQuery,
  type FinalSummaryContent,
  type PreviewFinalSummary,
} from "@/api/previewFinalSummary";
import { useCapability } from "@/auth/SessionContext";
import { downloadTextFile } from "@/lib/downloadFile";
import {
  FINAL_SUMMARY_TEXT_SECTIONS,
  finalSummaryFilename,
  finalSummaryMarkdown,
} from "@/lib/labFinalSummary";
import { Button } from "@/ui/Button";
import { Dialog, DialogContent } from "@/ui/Dialog";

function buttonLabel(summary: PreviewFinalSummary | undefined): string {
  switch (summary?.status) {
    case "queued":
    case "processing":
      return "Final summary · Creating…";
    case "completed":
      return "Final summary · Ready";
    case "failed":
      return "Final summary · Failed";
    case "expired":
      return "Final summary · Expired";
    default:
      return "Final summary";
  }
}

function statusLine(summary: PreviewFinalSummary): string {
  switch (summary.status) {
    case "queued":
    case "processing":
      return "Creating the summary…";
    case "failed":
      return "The summary could not be created.";
    case "expired":
      return "The summary has expired. Run a new test to get another.";
    default:
      return summary.sessionEnded
        ? "The summary has not been created yet."
        : "The summary is created automatically when the conversation ends.";
  }
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="space-y-1">
      <h3 className="text-sm font-semibold">{title}</h3>
      {children}
    </section>
  );
}

function Text({ value }: { value: string }) {
  return (
    <p dir="auto" className="whitespace-pre-wrap text-sm">
      {value}
    </p>
  );
}

function Pairs({ entries }: { entries: [string, string][] }) {
  return (
    <dl className="space-y-1 text-sm">
      {entries.map(([key, value]) => (
        <div key={key}>
          <dt dir="auto" className="font-medium">
            {key}
          </dt>
          <dd dir="auto" className="whitespace-pre-wrap">
            {value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function SummaryBody({ content }: { content: FinalSummaryContent }) {
  const background = Object.entries(content.backgroundSections ?? {}).filter(
    ([, value]) => value.trim() !== "",
  );
  const findings = Object.entries(content.diagnosticCriteriaFindings ?? {})
    .map(([track, items]): [string, [string, string][]] => [
      track,
      Object.entries(items).filter(([, value]) => value.trim() !== ""),
    ])
    .filter(([, items]) => items.length > 0);
  const diagnoses = (
    [
      ["Primary diagnoses", content.indicatedDiagnoses?.primary],
      ["Secondary diagnoses", content.indicatedDiagnoses?.secondary],
    ] as const
  ).filter(([, group]) => group !== undefined && Object.keys(group).length > 0);

  return (
    <div className="space-y-4">
      {FINAL_SUMMARY_TEXT_SECTIONS.map(([key, title]) => {
        const value = content[key];
        return typeof value === "string" && value.trim() !== "" ? (
          <Section key={key} title={title}>
            <Text value={value} />
          </Section>
        ) : null;
      })}
      {background.length > 0 ? (
        <Section title="Background">
          <Pairs entries={background} />
        </Section>
      ) : null}
      {findings.map(([track, items]) => (
        <Section key={track} title={`Findings: ${track}`}>
          <Pairs entries={items} />
        </Section>
      ))}
      {diagnoses.map(([title, group]) => (
        <Section key={title} title={title}>
          <Pairs
            entries={Object.entries(group ?? {}).map(([track, names]) => [
              track,
              names.join(", "),
            ])}
          />
        </Section>
      ))}
    </div>
  );
}

function FinalSummaryDialog({
  summary,
  isPending,
  projectId,
  runRef,
  onClose,
}: {
  summary: PreviewFinalSummary | undefined;
  isPending: boolean;
  projectId: string;
  runRef: string;
  onClose: () => void;
}) {
  const end = useEndPreviewFinalSummaryMutation(projectId, runRef);
  const canEnd = useCapability("preview.create");
  const [confirming, setConfirming] = useState(false);
  const canRequest =
    summary !== undefined &&
    canEnd &&
    summary.status === "not_generated" &&
    !summary.sessionEnded;
  const canRetry =
    summary?.status === "failed" && summary.retryable === true && canEnd;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title="Final summary"
        description="The clinical summary Liv produced for this run. Synthetic test output, not a clinical document."
        className="w-[min(44rem,calc(100vw-2rem))]"
        footer={
          <>
            {summary?.status === "completed" && summary.summary ? (
              <Button
                variant="outline"
                onClick={() =>
                  downloadTextFile(
                    finalSummaryFilename(runRef, new Date()),
                    finalSummaryMarkdown(summary.summary!),
                    "text/markdown",
                  )
                }
              >
                Download
              </Button>
            ) : null}
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </>
        }
      >
        {isPending ? (
          <p role="status" className="text-sm text-muted-foreground">
            Loading the summary…
          </p>
        ) : summary === undefined ? (
          <p role="alert" className="text-sm text-destructive">
            The summary could not be read. Try again in a moment.
          </p>
        ) : summary.status === "completed" && summary.summary ? (
          <SummaryBody content={summary.summary} />
        ) : (
          <div className="space-y-3">
            <p role="status" className="text-sm">
              {end.isPending ? "Creating the summary…" : statusLine(summary)}
            </p>
            {canRetry && !end.isPending ? (
              <Button variant="outline" size="sm" onClick={() => end.mutate()}>
                Try again
              </Button>
            ) : null}
            {canRequest && !confirming ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setConfirming(true)}
              >
                End conversation &amp; create summary
              </Button>
            ) : null}
            {canRequest && confirming ? (
              <div className="flex flex-wrap items-center gap-3">
                <p className="text-sm">
                  This ends the conversation for this run.
                </p>
                <Button
                  size="sm"
                  disabled={end.isPending}
                  onClick={() =>
                    end.mutate(undefined, {
                      onSettled: () => setConfirming(false),
                    })
                  }
                >
                  End conversation
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={end.isPending}
                  onClick={() => setConfirming(false)}
                >
                  Cancel
                </Button>
              </div>
            ) : null}
          </div>
        )}
        {end.isError ? (
          <p role="alert" className="mt-3 text-sm text-destructive">
            {end.error instanceof ApiError
              ? end.error.message
              : "The conversation could not be ended. Try again in a moment."}
          </p>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/** "Final summary · <status>": opens the run's final clinical summary in a dialog. */
export function LabFinalSummaryButton({
  projectId,
  runRef,
  runEnded = false,
}: {
  projectId: string;
  runRef: string;
  runEnded?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const query = usePreviewFinalSummaryQuery(projectId, runRef, runEnded);
  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        {buttonLabel(query.data)}
      </Button>
      {open ? (
        <FinalSummaryDialog
          summary={query.data}
          isPending={query.isPending}
          projectId={projectId}
          runRef={runRef}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </>
  );
}
